import React from 'react';
import { createStackNavigator, TransitionPresets } from '@react-navigation/stack';
import { screenNames } from './screenNames';
import { AppbarScreenOptions, DefaultModalOptions, DefaultScreenOptions } from '../components/NavigatorAppbar';
import { useI18n } from '../context/I18nContext';
import { MapView, MapViewRouteParams } from '../screens/MapView';
import QRScanScreen, { QRScreenRouteParamsType } from '../screens/QRScanScreen';
import EnterCodeManuallyScreen from '../screens/EnterCodeManuallyScreen';
import SelectPaymentMethod from '../payment/screens/SelectPaymentMethod';
import BookingDetail, { BookingDetailRouteParams } from '../screens/BookingDetail';
import AddPaymentMethod from '../payment/screens/AddPaymentMethod';
import { SpaceInfo, SpaceInfoRouteParams } from '../screens/SpaceInfo';
import OnlinePage, { OnlinePageRouteParams } from '../screens/OnlinePage';
import { MapScreenOptions } from '../components/MapFilterAppbar';
import { SecurityDepositDialog, SecurityDepositScreenRouteParams } from '../screens/SecurityDepositDialog';
import { ErrorDialog, ErrorDialogRouteParams } from '../screens/CommonDialogs';
import {
  ConfirmReservationDialog,
  ConfirmReservationDialogRouteParams,
} from '../payment/screens/ConfirmReservationDialog';
import EndReservationDialog, { EndReservationDialogRouteParams } from '../payment/screens/EndReservationDialog';
import ExtendReservationDialog, {
  ExtendReservationDialogRouteParams,
} from '../payment/screens/ExtendReservationDialog';
import {
  CancelReservationDialog,
  CancelReservationDialogRouteParams,
} from '../payment/screens/CancelReservationDialog';
import { TimezoneWarningDialog } from '../screens/TimezoneWarningDialog';
import { ChooseStoreDialog } from '../screens/ChooseStoreDialog';
import { SubscriptionsRouteParams, SubscriptionsScreen } from '../screens/Subscriptions/SubscriptionsScreen';
import {
  SubscriptionDetailRouteParams,
  SubscriptionDetailScreen,
} from '../screens/Subscriptions/SubscriptionDetailScreen';
import {
  ConfirmSubscriptionCancelDialog,
  ConfirmSubscriptionCancelDialogRouteParams,
} from '../screens/Subscriptions/ConfirmSubscriptionCancelDialog';
import {
  ConfirmSubscriptionDialog,
  ConfirmSubscriptionDialogRouteParams,
} from '../screens/Subscriptions/ConfirmSubscriptionDialog';
import { LockerListingRouteParams, LockerListingScreen } from '../screens/OneTimePurchaseProducts/LockerListingScreen';
import {
  OneTimePurchaseProductsRouteParams,
  OneTimePurchaseProductsScreen,
} from '../screens/OneTimePurchaseProducts/OneTimePurchaseProductsScreen';
import {
  OneTimePurchaseProductDetailRouteParams,
  OneTimePurchaseProductDetailScreen,
} from '../screens/OneTimePurchaseProducts/OneTimePurchaseProductDetailScreen';
import {
  ConfirmOneTimePurchaseDialog,
  ConfirmOneTimePurchaseDialogRouteParams,
} from '../screens/OneTimePurchaseProducts/ConfirmOneTimePurchaseDialog';
import {
  ConfirmOneTimePurchaseCancelDialog,
  ConfirmOneTimePurchaseCancelDialogRouteParams,
} from '../screens/OneTimePurchaseProducts/ConfirmOneTimePurchaseCancelDialog';
import {
  ReservationProductDetailRouteParams,
  ReservationProductDetailScreen,
} from '../screens/OneTimePurchaseProducts/ReservationProductDetailScreen';
import {
  LockerReservationDetailRouteParams,
  LockerReservationDetailScreen,
} from '../screens/LockerReservationDetailScreen';
import {
  ConfirmReservationProductDialog,
  ConfirmReservationProductDialogRouteParams,
} from '../screens/OneTimePurchaseProducts/ConfirmReservationProductDialog';
import {
  ConfirmReservationProductEndDialog,
  ConfirmReservationProductEndDialogRouteParams,
} from '../screens/OneTimePurchaseProducts/ConfirmReservationProductEndDialog';
import ThirdPartyAuthWebView, { ThirdPartyAuthWebViewRouteParams } from '../screens/ThirdPartyAuthWebView';
import AddThirdPartyAuth from '../screens/UpdateProfile/AddThirdPartyAuth';
import { ThirdPartyAuthDetail, ThirdPartyAuthDetailRouteParams } from '../screens/UpdateProfile/ThirdPartyAuthDetail';

export type MapStackRouteParams = {
  [screenNames.Map]: undefined | MapViewRouteParams;
  [screenNames.MapScheduleFilter]: undefined;
  [screenNames.MapBookForLater]: undefined;
  [screenNames.AddPaymentMethod]: undefined;
  [screenNames.QRScanScreen]: QRScreenRouteParamsType;
  [screenNames.EnterCodeManuallyScreen]: QRScreenRouteParamsType;
  [screenNames.PaymentChangeBeforeCheckoutView]: undefined;
  [screenNames.BookingDetail]: BookingDetailRouteParams;
  [screenNames.AddThirdPartyAuth]: undefined;
  [screenNames.ThirdPartyAuthWebView]: ThirdPartyAuthWebViewRouteParams;
  [screenNames.ThirdPartyAuthDetail]: ThirdPartyAuthDetailRouteParams;
  [screenNames.SubscriptionsScreen]: SubscriptionsRouteParams;
  [screenNames.SubscriptionDetailScreen]: SubscriptionDetailRouteParams;
  [screenNames.ConfirmSubscriptionDialog]: ConfirmSubscriptionDialogRouteParams;
  [screenNames.ParkingTerms]: OnlinePageRouteParams | undefined;
  [screenNames.ConfirmSubscriptionCancelDialog]: ConfirmSubscriptionCancelDialogRouteParams;
  [screenNames.LockerListingScreen]: LockerListingRouteParams;
  [screenNames.OneTimePurchaseProductsScreen]: OneTimePurchaseProductsRouteParams;
  [screenNames.OneTimePurchaseProductDetailScreen]: OneTimePurchaseProductDetailRouteParams;
  [screenNames.ConfirmOneTimePurchaseDialog]: ConfirmOneTimePurchaseDialogRouteParams;
  [screenNames.ConfirmOneTimePurchaseCancelDialog]: ConfirmOneTimePurchaseCancelDialogRouteParams;
  [screenNames.ReservationProductDetailScreen]: ReservationProductDetailRouteParams;
  [screenNames.LockerReservationDetailScreen]: LockerReservationDetailRouteParams;
  [screenNames.ConfirmReservationProductDialog]: ConfirmReservationProductDialogRouteParams;
  [screenNames.ConfirmReservationProductEndDialog]: ConfirmReservationProductEndDialogRouteParams;
  [screenNames.SpaceInfo]: SpaceInfoRouteParams;
  [screenNames.SpaceDescription]: OnlinePageRouteParams | undefined;
  [screenNames.SecurityDepositDialog]: SecurityDepositScreenRouteParams;
  [screenNames.ConfirmReservationDialog]: ConfirmReservationDialogRouteParams;
  [screenNames.EndReservationDialog]: EndReservationDialogRouteParams;
  [screenNames.ExtendReservationDialog]: ExtendReservationDialogRouteParams;
  [screenNames.CancelReservationDialog]: CancelReservationDialogRouteParams;
  [screenNames.ErrorDialog]: ErrorDialogRouteParams;
  [screenNames.TimezoneWarningDialog]: undefined;
  [screenNames.ChooseStoreDialog]: undefined;
};

const Stack = createStackNavigator<MapStackRouteParams>();

export function MapStackNavigator() {
  const { I18n } = useI18n();
  return (
    <Stack.Navigator initialRouteName={screenNames.Map} screenOptions={MapScreenOptions}>
      <Stack.Screen name={screenNames.Map} component={MapView} options={MapScreenOptions} />
      <Stack.Screen
        name={screenNames.AddPaymentMethod}
        component={AddPaymentMethod}
        options={{
          ...AppbarScreenOptions,
          title: I18n.t('payment.addACard'),
          ...TransitionPresets.ModalSlideFromBottomIOS,
        }}
      />
      <Stack.Screen
        name={screenNames.QRScanScreen}
        component={QRScanScreen}
        options={{
          ...DefaultScreenOptions,
          title: I18n.t('mapview.scanQr.headerTitle'),
        }}
      />
      <Stack.Screen
        name={screenNames.EnterCodeManuallyScreen}
        component={EnterCodeManuallyScreen}
        options={DefaultScreenOptions}
      />
      <Stack.Screen
        name={screenNames.PaymentChangeBeforeCheckoutView}
        component={SelectPaymentMethod}
        options={{
          ...DefaultScreenOptions,
          title: I18n.t('payment.changePayment'),
        }}
      />
      <Stack.Screen
        name={screenNames.BookingDetail}
        component={BookingDetail}
        options={{
          ...DefaultScreenOptions,
          title: I18n.t('bookings.headerTitle'),
        }}
      />

      <Stack.Screen
        name={screenNames.AddThirdPartyAuth}
        component={AddThirdPartyAuth}
        options={{
          ...DefaultScreenOptions,
          title: I18n.t('thirdPartyAuth.add.headerTitle'),
        }}
      />
      <Stack.Screen
        name={screenNames.ThirdPartyAuthWebView}
        component={ThirdPartyAuthWebView}
        options={AppbarScreenOptions}
      />
      <Stack.Screen
        name={screenNames.ThirdPartyAuthDetail}
        component={ThirdPartyAuthDetail}
        options={({ route }) => ({
          ...AppbarScreenOptions,
          title: I18n.t(`thirdPartyAuth.provider.${route.params.authProvider}`),
        })}
      />

      <Stack.Screen
        name={screenNames.SubscriptionsScreen}
        component={SubscriptionsScreen}
        options={{
          ...DefaultScreenOptions,
          title: I18n.t('subscriptions.title'),
        }}
      />
      <Stack.Screen
        name={screenNames.SubscriptionDetailScreen}
        component={SubscriptionDetailScreen}
        options={{
          ...DefaultScreenOptions,
          title: I18n.t('subscriptions.detail.title'),
        }}
      />
      <Stack.Screen
        name={screenNames.ConfirmSubscriptionDialog}
        component={ConfirmSubscriptionDialog}
        options={DefaultModalOptions}
      />
      <Stack.Screen name={screenNames.ParkingTerms} component={OnlinePage} options={AppbarScreenOptions} />
      <Stack.Screen
        name={screenNames.ConfirmSubscriptionCancelDialog}
        component={ConfirmSubscriptionCancelDialog}
        options={DefaultModalOptions}
      />
      <Stack.Screen
        name={screenNames.LockerListingScreen}
        component={LockerListingScreen}
        options={{
          ...DefaultScreenOptions,
          title: I18n.t('lockerInfo.list.title'),
        }}
      />
      <Stack.Screen
        name={screenNames.OneTimePurchaseProductsScreen}
        component={OneTimePurchaseProductsScreen}
        options={{
          ...DefaultScreenOptions,
          title: I18n.t('oneTimePurchase.title'),
        }}
      />
      <Stack.Screen
        name={screenNames.OneTimePurchaseProductDetailScreen}
        component={OneTimePurchaseProductDetailScreen}
        options={{
          ...DefaultScreenOptions,
          title: I18n.t('oneTimePurchase.detail.title'),
        }}
      />
      <Stack.Screen
        name={screenNames.ConfirmOneTimePurchaseDialog}
        component={ConfirmOneTimePurchaseDialog}
        options={DefaultModalOptions}
      />
      <Stack.Screen
        name={screenNames.ConfirmOneTimePurchaseCancelDialog}
        component={ConfirmOneTimePurchaseCancelDialog}
        options={DefaultModalOptions}
      />
      <Stack.Screen
        name={screenNames.ReservationProductDetailScreen}
        component={ReservationProductDetailScreen}
        options={{
          ...DefaultScreenOptions,
          title: I18n.t('reservation.detail.title'),
        }}
      />
      <Stack.Screen
        name={screenNames.LockerReservationDetailScreen}
        component={LockerReservationDetailScreen}
        options={{
          ...DefaultScreenOptions,
          title: I18n.t('lockerInfo.reservation.detail.title'),
        }}
      />
      <Stack.Screen
        name={screenNames.ConfirmReservationProductDialog}
        component={ConfirmReservationProductDialog}
        options={DefaultModalOptions}
      />
      <Stack.Screen
        name={screenNames.ConfirmReservationProductEndDialog}
        component={ConfirmReservationProductEndDialog}
        options={DefaultModalOptions}
      />
      <Stack.Screen
        name={screenNames.SpaceInfo}
        component={SpaceInfo}
        options={{
          ...DefaultScreenOptions,
          title: I18n.t('mapview.spaceInfo.headerTitle'),
        }}
      />
      <Stack.Screen
        name={screenNames.SpaceDescription}
        component={OnlinePage}
        options={{
          ...AppbarScreenOptions,
          title: I18n.t('mapview.spaceInfo.webpage.title'),
        }}
      />
      <Stack.Screen
        name={screenNames.SecurityDepositDialog}
        component={SecurityDepositDialog}
        options={DefaultModalOptions}
      />
      <Stack.Screen
        name={screenNames.ConfirmReservationDialog}
        component={ConfirmReservationDialog}
        options={DefaultModalOptions}
      />
      <Stack.Screen
        name={screenNames.EndReservationDialog}
        component={EndReservationDialog}
        options={DefaultModalOptions}
      />
      <Stack.Screen
        name={screenNames.ExtendReservationDialog}
        component={ExtendReservationDialog}
        options={DefaultModalOptions}
      />
      <Stack.Screen
        name={screenNames.CancelReservationDialog}
        component={CancelReservationDialog}
        options={DefaultModalOptions}
      />
      <Stack.Screen name={screenNames.ErrorDialog} component={ErrorDialog} options={DefaultModalOptions} />
      <Stack.Screen
        name={screenNames.TimezoneWarningDialog}
        component={TimezoneWarningDialog}
        options={DefaultModalOptions}
      />
      <Stack.Screen name={screenNames.ChooseStoreDialog} component={ChooseStoreDialog} options={DefaultModalOptions} />
    </Stack.Navigator>
  );
}
